import { useEffect, useState, useRef } from 'react';
import { useDebounce } from '@ajaibid/hooks';
import useForm from './useForm';

export default (field, { defaultValue = '', debounceDelay = 200, validators = {} }) => {
  const mounted = useRef(false);
  const [showError, setShowError] = useState(false);
  const prevDefaultValue = useRef(defaultValue);
  const prevValidators = useRef(validators);

  const [
    ,
    {
      createField: createForm,
      updateField: updateForm,
      deleteField: deleteForm,
      getField,
      submitForm,
    },
  ] = useForm();
  const { value, error = false, errorKey = false } = getField(field);

  const [fieldValue, setFieldValue] = useState(defaultValue);
  const debouncedValue = useDebounce(fieldValue, debounceDelay);

  const handleResetField = () => {
    setFieldValue(defaultValue);
  };

  const handleUpdateField = (param) => {
    if (typeof param === 'function') {
      const returnedValue = param(fieldValue);
      setFieldValue(returnedValue);
    } else {
      setFieldValue(param);
    }
  };

  // Create field on mount
  useEffect(() => {
    if (!mounted.current && field) {
      createForm(field, defaultValue, validators);
      prevDefaultValue.current = defaultValue;
      prevValidators.current = validators;
    }
  }, [field, defaultValue]);

  // Update value on [defaultValue] change (e.g. due to api response, etc)
  useEffect(() => {
    if (prevDefaultValue.current !== defaultValue) {
      updateForm(field, defaultValue, validators);
      prevDefaultValue.current = defaultValue;
      setShowError(false);
    }
  }, [field, JSON.stringify(defaultValue)]);

  // Update validators on [validators] change (e.g. due to api response, etc)
  useEffect(() => {
    if (prevValidators.current !== validators) {
      updateForm(field, value, validators);
      prevValidators.current = validators;
    }
  }, [field, JSON.stringify(validators)]);
  // FIXME: validators has a function. If the func changes, the dependency
  // will not identify as JSON.stringify() ignores any functions.

  // Update field on value change [debouncedValue]
  useEffect(() => {
    if (field && mounted.current) {
      updateForm(field, debouncedValue, validators);
      setShowError(true);
    }

    mounted.current = true;
  }, [field, JSON.stringify(debouncedValue)]);

  useEffect(() => {
    if (mounted.current) {
      setFieldValue(value);
    }
  }, [value]);

  // Destroy field
  useEffect(
    () => () => {
      deleteForm(field);
    },
    [deleteForm, field]
  );

  return [
    fieldValue,
    {
      error: showError ? error : false,
      errorKey: showError ? errorKey : false,
      showError,
      setShowError,
      resetField: handleResetField,
      updateField: handleUpdateField,
      getField,
      updateForm,
      submitForm,
    },
  ];
};
